import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import type { SubParcelDto } from '@forest/contracts';
import { useAuthStore } from '@/stores/sessionStore';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import { useDeleteSubParcel, useSubParcels } from './useSubParcels';
import { SUBPARCEL_STROKE } from './SubParcelsMapLayer';

/**
 * A parcel's subpolygons, listed under its details. Delete mirrors the
 * server's `canDelete`: SUPER_ADMIN, or whoever imported the row (an
 * AJUTOR acts for their manager).
 */
export function SubParcelList({ parcelId, bare = false }: { parcelId: string; bare?: boolean }) {
  const { t } = useTranslation(['database', 'common']);
  const user = useAuthStore((s) => s.user);
  const pushToast = useUiStore((s) => s.pushToast);
  const { data } = useSubParcels(parcelId);
  const del = useDeleteSubParcel();
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const items = data?.items ?? [];
  if (items.length === 0) return null;

  const canDelete = (s: SubParcelDto) =>
    !!user &&
    (user.role === 'SUPER_ADMIN' ||
      (user.role === 'AJUTOR_ADMINISTRATOR' ? s.userId === user.managerId : s.userId === user.id));

  const total = items.reduce((sum, s) => sum + s.suprafata, 0);

  const campaignLabel = (s: SubParcelDto) =>
    s.sezon && s.anul
      ? `${t(`database:subparcels.sezon.${s.sezon}`, {
          defaultValue: s.sezon === 'PRIMAVARA' ? 'Primăvara' : 'Toamna',
        })} ${s.anul}`
      : '\u2014';

  const onDelete = async (s: SubParcelDto) => {
    try {
      await del.mutateAsync(s.id);
      setConfirmId(null);
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  return (
    <div className={bare ? '' : 'rounded border border-slate-200 dark:border-slate-700'}>
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-1.5 text-xs dark:border-slate-700">
        <span className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-200">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: SUBPARCEL_STROKE }}
            aria-hidden
          />
          {t('database:subparcels.title', {
            defaultValue: 'Subpoligoane ({{count}})',
            count: items.length,
          })}
        </span>
        <span className="tabular-nums text-slate-500">
          {t('database:subparcels.total', { defaultValue: 'Total {{ha}} ha', ha: total.toFixed(2) })}
        </span>
      </div>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-slate-500">
          <tr>
            <th className="px-3 py-1 text-left font-semibold">UCF</th>
            <th className="px-3 py-1 text-right font-semibold">
              {t('database:subparcels.area', { defaultValue: 'Suprafață (ha)' })}
            </th>
            <th className="px-3 py-1 text-left font-semibold">
              {t('database:subparcels.campaign', { defaultValue: 'Recepție' })}
            </th>
            <th className="px-3 py-1 text-left font-semibold">
              {t('database:subparcels.parcele', { defaultValue: 'Parcele' })}
            </th>
            <th className="w-24 px-3 py-1" />
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-t border-slate-100 dark:border-slate-800">
              <td className="px-3 py-1 font-mono font-semibold" style={{ color: SUBPARCEL_STROKE }}>
                {s.ucf}
              </td>
              <td className="px-3 py-1 text-right tabular-nums">{s.suprafata.toFixed(2)}</td>
              <td className="px-3 py-1 text-slate-600">{campaignLabel(s)}</td>
              <td className="px-3 py-1 font-mono text-[11px] text-slate-600" title={s.sourceFile ?? undefined}>
                {s.parcele ?? '\u2014'}
              </td>
              <td className="px-3 py-1 text-right">
                {canDelete(s) ? (
                  confirmId === s.id ? (
                    <button
                      type="button"
                      onClick={() => void onDelete(s)}
                      disabled={del.isPending}
                      className="rounded bg-red-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-red-700"
                    >
                      {t('database:subparcels.confirmDelete', { defaultValue: 'Confirmă' })}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmId(s.id)}
                      className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                      aria-label={t('common:action.delete')}
                      title={t('common:action.delete')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
